import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  runValidator,
  sanitizeDigits,
  validateAll,
  validateInvoiceFile,
  validateProductVideo,
} from "../../../Utils/validations";
import { validateVideoDuration } from "../../../Utils/maxVideoSizeValidations";
import { INITIAL_FORM_VALUES } from "../../../Constant/AllFormFields";
import { saveNeverOwnedDetailsAction } from "../../../Actions/saveFormDetailsAction";

const useHomeData = () => {
  const navigate = useNavigate();

  // ---------- field values / errors ----------
  const [values, setValues] = useState(INITIAL_FORM_VALUES);
  const [errors, setErrors] = useState({});
  const [confirmChecked, setConfirmChecked] = useState(false);

  // ---------- selected protection plan (from API) ----------
  const [selectedPlan, setSelectedPlan] = useState(null);

  // ---------- warranty type (kept for extended-field visibility) ----------
  const [warrantyType, setWarrantyType] = useState("brand");

  // ---------- invoice upload ----------
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [invoiceError, setInvoiceError] = useState("");
  const [isDragOver, setIsDragOver] = useState(false);

  // ---------- product video ----------
  const videoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const [isRecording, setIsRecording] = useState(false);
  const [hasRecordedVideo, setHasRecordedVideo] = useState(false);
  const [recordedVideoUrl, setRecordedVideoUrl] = useState(null);
  const [productVideoFile, setProductVideoFile] = useState(null);
  const [videoError, setVideoError] = useState("");
  const [cameraError, setCameraError] = useState("");
  const [cameraActive, setCameraActive] = useState(false);

  // ---------- submit ----------
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ---------------- plan selection ----------------
  const selectPlan = useCallback((plan) => {
    setSelectedPlan(plan);
    // Derive warrantyType from description for extended-field visibility
    const desc = (plan?.plan_Description || "").toLowerCase();
    if (desc.includes("extended")) setWarrantyType("extended");
    else if (desc.includes("annual") || desc.includes("maintenance"))
      setWarrantyType("amp");
    else setWarrantyType("brand");
  }, []);

  // kept for backward compat (chips inside extended card)
  const selectWarrantyType = useCallback((type) => setWarrantyType(type), []);
  const selectExtendedYears = useCallback((years) => {
    setValues((prev) => ({ ...prev, extendedYears: years }));
    setWarrantyType("extended");
  }, []);

  // ---------------- field handlers ----------------
  const handleFieldChange = useCallback((id, rawValue, sanitize) => {
    const value =
      sanitize === "digits"
        ? sanitizeDigits(rawValue, id === "mobileNumber" ? 10 : 6)
        : rawValue;

    setValues((prev) => {
      const next = { ...prev, [id]: value };
      if (id === "state") next.city = ""; // reset city on state change
      return next;
    });

    setErrors((prev) => {
      if (!prev[id]) return prev;
      const result = runValidator(id, value);
      const next = { ...prev };
      if (result === true) delete next[id];
      else next[id] = result;
      return next;
    });
  }, []);

  const handleFieldBlur = useCallback((id) => {
    setValues((prev) => {
      const result = runValidator(id, prev[id]);
      setErrors((prevErrors) => {
        const next = { ...prevErrors };
        if (result === true) delete next[id];
        else next[id] = result;
        return next;
      });
      return prev;
    });
  }, []);

  // ---------------- invoice upload ----------------
  const handleInvoiceFile = useCallback((file) => {
    const result = validateInvoiceFile(file);
    if (result !== true) {
      setInvoiceError(result);
      toast.error(result);
      return;
    }
    setInvoiceError("");
    setInvoiceFile(file);
    toast.success(`Invoice attached: ${file.name}`);
  }, []);

  const onDrop = useCallback(
    (e) => {
      e.preventDefault();
      setIsDragOver(false);
      const file = e.dataTransfer.files?.[0];
      if (file) handleInvoiceFile(file);
    },
    [handleInvoiceFile],
  );
  const onDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);
  const onDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);
  const onInvoiceInputChange = useCallback(
    (e) => {
      const file = e.target.files?.[0];
      if (file) handleInvoiceFile(file);
    },
    [handleInvoiceFile],
  );

  // ---------------- camera / video ----------------
  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  const startCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
        audio: true,
      });
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
      setCameraActive(true);
      setHasRecordedVideo(false);
      setCameraError("");
      setVideoError("");
      return true;
    } catch {
      const msg = "Camera access denied or unavailable. Use Upload instead.";
      setCameraError(msg);
      toast.error(msg);
      return false;
    }
  }, []);

  const startRecording = useCallback(() => {
    if (!streamRef.current) return;
    recordedChunksRef.current = [];
    const mimeType = MediaRecorder.isTypeSupported("video/webm;codecs=vp9,opus")
      ? "video/webm;codecs=vp9,opus"
      : "video/webm";
    const recorder = new MediaRecorder(streamRef.current, { mimeType });
    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) recordedChunksRef.current.push(e.data);
    };
    recorder.onstop = async () => {
      const blob = new Blob(recordedChunksRef.current, { type: mimeType });

      const isValid = await validateVideoDuration(blob);
      if (!isValid) {
        // toast already fired — discard the recording, restore camera-ready state
        setIsRecording(false);
        setCameraActive(false);
        stopStream();
        return;
      }

      setProductVideoFile(blob);
      setVideoError("");
      const url = URL.createObjectURL(blob);
      setRecordedVideoUrl(url);
      setHasRecordedVideo(true);
      setCameraActive(false);
      stopStream();
      setIsRecording(false);
      toast.success("Video recorded successfully");
    };
    recorder.start();
    mediaRecorderRef.current = recorder;
    setIsRecording(true);
  }, [stopStream]);

  const stopRecording = useCallback(() => {
    mediaRecorderRef.current?.stop();
  }, []);

  const handleRecordClick = useCallback(async () => {
    if (isRecording) {
      stopRecording();
      return;
    }
    if (!streamRef.current) {
      const ok = await startCamera();
      if (!ok) return;
    }
    startRecording();
  }, [isRecording, startCamera, startRecording, stopRecording]);

  const handleVideoUpload = useCallback(
    async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;

      // Reset input so the same file can be re-selected after rejection
      e.target.value = "";

      const isValid = await validateVideoDuration(file);
      if (!isValid) return; // toast already fired inside validateVideoDuration

      setProductVideoFile(file);
      setVideoError("");
      const url = URL.createObjectURL(file);
      setRecordedVideoUrl(url);
      setHasRecordedVideo(true);
      setCameraActive(false);
      setCameraError("");
      stopStream();
      toast.success(`Video uploaded: ${file.name}`);
    },
    [stopStream],
  );

  useEffect(() => {
    if (cameraActive && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
    }
  }, [cameraActive]);

  useEffect(() => {
    return () => {
      stopStream();
      if (recordedVideoUrl) URL.revokeObjectURL(recordedVideoUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------------- derived: summary ----------------
  const productLabel = `${values.brandName}`.trim();
  // ${values.productCategory}`.trim();

  // Helper: convert validity months to readable year string for summary
  const extYearLabel = (() => {
    if (!selectedPlan?.extendedVariantId) return "";
    // Find the extended variant's validity from all plans stored in selectPlan calls
    // The extendedVariantId is set from extendedGroups — validity stored in selectedPlan
    const months = selectedPlan?.extendedValidity;
    if (!months) return "";
    const m = Number(months);
    if (m % 12 === 0) {
      const y = m / 12;
      return y === 1 ? " (1 Year)" : ` (${y} Years)`;
    }
    return ` (${m} Months)`;
  })();

  const warrantySummary = selectedPlan
    ? selectedPlan.extendedVariantId
      ? `${selectedPlan.plan_Description} + Extended Warranty${extYearLabel}`
      : selectedPlan.brandWarrantyYear
        ? `${selectedPlan.plan_Description} + ${selectedPlan.brandWarrantyYear} Year Brand Warranty`
        : selectedPlan.plan_Description
    : warrantyType === "brand"
      ? "Brand Warranty (1 Year)"
      : warrantyType === "extended"
        ? `Extended Warranty (${values.extendedYears})`
        : warrantyType === "amp"
          ? "Annual Maintenance Plan"
          : "—";

  // ---------------- derived: form readiness ----------------
  // All field validators pass + invoice + video + confirm checkbox
  const { isValid: fieldsValid } = validateAll(values);
  const isFormReady =
    fieldsValid &&
    validateInvoiceFile(invoiceFile) === true &&
    validateProductVideo(productVideoFile) === true &&
    confirmChecked;

  // ---------------- submit ----------------
  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      // ── Validate fields ──────────────────────────────────────────────
      const { isValid, errors: fieldErrors } = validateAll(values);
      setErrors(fieldErrors);

      const invoiceResult = validateInvoiceFile(invoiceFile);
      if (invoiceResult !== true) setInvoiceError(invoiceResult);

      const videoResult = validateProductVideo(productVideoFile);
      if (videoResult !== true) setVideoError(videoResult);

      const allValid =
        isValid &&
        invoiceResult === true &&
        videoResult === true &&
        confirmChecked;

      if (!allValid) {
        const issues = [];
        if (!isValid) issues.push("Please fill in all required fields");
        if (invoiceResult !== true) issues.push(invoiceResult);
        if (videoResult !== true) issues.push(videoResult);
        if (!confirmChecked)
          issues.push("Please accept the Terms & Privacy Policy");

        toast.error(
          <div>
            <strong>Please fix the following:</strong>
            <ul style={{ margin: "6px 0 0", paddingLeft: "1.1rem" }}>
              {issues.map((msg, i) => (
                <li key={i}>{msg}</li>
              ))}
            </ul>
          </div>,
          { autoClose: 6000 },
        );

        const firstErrorKey = Object.keys(fieldErrors)[0];
        if (firstErrorKey) {
          document
            .getElementById(firstErrorKey)
            ?.scrollIntoView({ behavior: "smooth", block: "center" });
        }
        return;
      }

      // ── Convert files to base64 then build & send JSON payload ───────
      setIsSubmitting(true);

      try {
        // Helper: File | Blob → pure base64 string (no data-URI prefix)
        const toBase64 = (file) =>
          new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
              // result is "data:<mime>;base64,<data>" — strip everything up to and including "base64,"
              const result = reader.result;
              const base64Index = result.indexOf("base64,");
              const base64 =
                base64Index !== -1 ? result.slice(base64Index + 7) : "";
              resolve(base64);
            };
            reader.onerror = () => reject(reader.error);
            reader.readAsDataURL(file);
          });

        // Convert both files in parallel
        const [invoiceBase64, videoBase64] = await Promise.all([
          invoiceFile ? toBase64(invoiceFile) : Promise.resolve(""),
          productVideoFile ? toBase64(productVideoFile) : Promise.resolve(""),
        ]);

        // Exact JSON body — files as base64 strings
        // Extract invoice image extension (e.g. ".png", ".jpg", ".pdf")
        const getExtension = (file) => {
          if (!file) return "";
          const name = file instanceof File ? file.name : "";
          const lastDot = name.lastIndexOf(".");
          return lastDot !== -1 ? name.slice(lastDot) : "";
        };

        const invoiceExtension = getExtension(invoiceFile);

        const payload = {
          StoreName: values.storeName?.trim() || "",
          CustomerName: values.customerName?.trim() || "",
          MobileNumber: values.mobileNumber?.trim() || "",
          EmailAddress: values.email?.trim() || "",
          City: values.city?.trim() || "",
          PinCode: values.pinCode?.trim() || "",
          State: values.state?.trim() || "",
          Plan: selectedPlan?.extendedVariantId
            ? `${selectedPlan.plan_VarriantsId},${selectedPlan.extendedVariantId}`
            : String(selectedPlan?.plan_VarriantsId || ""),
          // Status: "PendingVerification",
          InoviceImg: invoiceBase64,
          ProductVideo: videoBase64,
          Extension: invoiceExtension,
          ProductCategory: values.productCategory?.trim() || "",
          BrandName: values.brandName?.trim() || "",
          SerialNumber: values.serialNumber?.trim() || "",
          InvoiceValue: values.invoiceValue?.trim() || "",
          // Extended warranty year selected by checkbox+radio (Never-Owned flow)
          // ExtendedWarrantyYear: selectedPlan?.extendedVariantId
          //   ? String(selectedPlan?.extendedValidity || "")
          //   : "",
          // Existing brand warranty year from dropdown (Extended Warranty card flow)
          ExistingWarrantyYear: selectedPlan?.brandWarrantyYear || "", // TODO:
        };

        // ── Call save API (JSON, Content-Type: application/json) ──────
        const result = await saveNeverOwnedDetailsAction(payload);

        // console.log("✅ Save API Response:", result);

        if (result.success) {
          navigate("/success", {
            state: {
              customerName: values.customerName,
              email: values.email,
              mobileNumber: values.mobileNumber,
              city: values.city,
              state: values.state,
              pinCode: values.pinCode,
              productLabel,
              warrantySummary,
              invoiceFileName: invoiceFile?.name || null,
              hasProductVideo: !!productVideoFile,
              registeredAt: new Date().toISOString(),
              customerId: result.data?.customerid || null,
            },
          });
        }
        // toast success/error handled inside saveNeverOwnedDetailsAction
      } catch (error) {
        // console.error("Unexpected submission error:", error);
        toast.error(error.message || "Something went wrong. Please try again.");
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      values,
      selectedPlan,
      invoiceFile,
      productVideoFile,
      confirmChecked,
      navigate,
      productLabel,
      warrantySummary,
    ],
  );

  return {
    values,
    errors,
    handleFieldChange,
    handleFieldBlur,
    confirmChecked,
    setConfirmChecked,

    // plan
    selectedPlan,
    selectPlan,

    // legacy warranty type helpers
    warrantyType,
    selectWarrantyType,
    selectExtendedYears,

    // invoice
    invoiceFile,
    invoiceError,
    isDragOver,
    onDrop,
    onDragOver,
    onDragLeave,
    onInvoiceInputChange,

    // video
    videoRef,
    isRecording,
    hasRecordedVideo,
    recordedVideoUrl,
    videoError,
    cameraError,
    cameraActive,
    handleRecordClick,
    handleVideoUpload,

    // summary
    productLabel,
    warrantySummary,

    // submit
    isSubmitting,
    isFormReady,
    handleSubmit,
  };
};

export default useHomeData;
